import { faCheck, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Menu,
  MenuItem,
  Stack,
  Box,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { clone, map } from 'lodash';
import { useReducer, useState, useRef } from 'react';

import { Button, type ButtonProps } from '../button/button';

interface Option {
  label: string;
  value: string;
  isActive?: boolean;
}

export interface DropdownMultiSelectProps
  extends Pick<ButtonProps, 'content' | 'icon' | 'size'> {
  title: string;
  options: Option[];
  onApply: (options: Option[]) => void;
  resetSelections?: boolean;
}

function filterReducer(
  allFilters: Option[],
  action:
    | { type: 'add_option' | 'remove_option'; idx: number }
    | { type: 'reset'; initialState: Option[] },
): Option[] {
  switch (action.type) {
    case 'add_option':
      allFilters[action.idx].isActive = true;
      break;
    case 'remove_option':
      allFilters[action.idx].isActive = false;
      break;
    case 'reset':
      allFilters = action.initialState;
      break;

    default:
      throw Error('Unknown action.');
  }

  // cloning to trigger state change
  return clone(allFilters);
}

export function DropdownMultiSelect({
  content,
  icon,
  onApply,
  options,
  resetSelections,
  title,
  size,
}: DropdownMultiSelectProps) {
  const [shown, setShown] = useState<boolean>(false);
  const [allOptions, dispatchOptionAction] = useReducer(filterReducer, options);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setShown(!shown);
  };

  const handleClose = () => {
    setShown(false);
  };

  return (
    <Box display="inline-block">
      <Button
        ref={anchorRef}
        onClick={handleToggle}
        content={title}
        icon={icon}
        size={size}
      />
      <Menu
        anchorEl={anchorRef.current}
        open={shown}
        onClose={handleClose}
        PaperProps={{
          sx: { minWidth: 200 },
        }}
      >
        {map(allOptions, (option, idx) => (
          <MenuItem
            key={idx}
            onClick={() => {
              dispatchOptionAction({
                type: option.isActive ? 'remove_option' : 'add_option',
                idx,
              });
            }}
          >
            <Box display="flex" alignItems="center">
              <Box sx={{ minWidth: '25px' }}>
                {option.isActive && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    style={{ marginLeft: '4px' }}
                    color="primary"
                  />
                )}
              </Box>
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          </MenuItem>
        ))}
        <Divider />
        <Box p={1}>
          <Stack direction="row" spacing={1} justifyContent="center">
            {resetSelections && (
              <Button
                content="Reset"
                variant="ghost"
                icon={faUndo}
                size="sm"
                onClick={() => {
                  dispatchOptionAction({
                    type: 'reset',
                    initialState: map(options, (o) => ({
                      ...o,
                      isActive: true,
                    })),
                  });
                }}
              />
            )}
            <Button
              content={content}
              icon={icon}
              size="sm"
              onClick={() => {
                onApply(allOptions);
                handleClose();
              }}
            />
          </Stack>
        </Box>
      </Menu>
    </Box>
  );
}
